import React, {memo, useCallback, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {translate} from '@mapsight/ui/src/js/helpers/i18n';
import modClasses from '@mapsight/ui/src/js/helpers/mod-classes';

import {selectExclusively} from '@mapsight/core/lib/feature-selections/actions';

import {FEATURE_SELECTIONS, FEATURE_SOURCES} from '@mapsight/ui/src/js/config/constants/controllers';
import {FEATURE_SELECTION_PRESELECT, FEATURE_SELECTION_SELECT} from '@mapsight/ui/src/js/config/feature/selections';

import {useNavPos} from '../../hooks';
import {
	createPreselectionSelectionIndicesSelector,
	lastOnTourSelectionIdSelector,
	preselectFeatureSelector
} from '../../mapsight-glue/store/selectors';


function PoiCycling() {
	const dispatch = useDispatch();
	const navPos = useNavPos();
	const appSource = useSelector(state => state[FEATURE_SOURCES][navPos?.app?.uri || '']);
	const selections = useSelector(createPreselectionSelectionIndicesSelector(appSource));
	const lastOnTour = useSelector(lastOnTourSelectionIdSelector);
	const detailsFeature = useSelector(preselectFeatureSelector);
	const goZero = navPos.home || (navPos.list && !detailsFeature);

	const targetSelection = navPos.deepLink || navPos.home ?
		FEATURE_SELECTION_SELECT : FEATURE_SELECTION_PRESELECT;

	const isPoiOnTour = useMemo(
		() => detailsFeature?.id && appSource?.ids && appSource.ids.includes(detailsFeature.id) || false,
		[detailsFeature?.id, appSource?.ids]
	);

	const selectCurr = useCallback(() => {
			if (!appSource?.ids || !appSource.ids.length) {
				return;
			}
			const targetId = goZero || (selections[FEATURE_SELECTION_PRESELECT].idx < 0 && !lastOnTour) ?
				appSource.ids[0] :
				lastOnTour || appSource.ids[selections[FEATURE_SELECTION_PRESELECT].idx];
			dispatch(selectExclusively(FEATURE_SELECTIONS, FEATURE_SELECTION_SELECT, targetId));
		},
		[goZero, appSource.ids, selections, lastOnTour, dispatch]
	);

	const selectNext = useCallback(() => {
			if (!appSource?.ids || !appSource.ids.length) {
				return;
			}
			const targetId = selections[FEATURE_SELECTION_PRESELECT].idx < 0 ?
				appSource.ids[0] :
				appSource.ids[(selections[FEATURE_SELECTION_PRESELECT].idx + 1) % selections[FEATURE_SELECTION_PRESELECT].length];
			dispatch(selectExclusively(
				FEATURE_SELECTIONS,
				targetSelection,
				targetId
			));
		},
		[targetSelection, appSource.ids, selections, dispatch]
	);
	const selectPrev = useCallback(() => {
			if (!appSource?.ids || !appSource.ids.length) {
				return;
			}
			const targetId = selections[FEATURE_SELECTION_PRESELECT].idx < 0 ?
				appSource.ids[0] :
				appSource.ids[
					(selections[FEATURE_SELECTION_PRESELECT].length + selections[FEATURE_SELECTION_PRESELECT].idx - 1)
					%
					selections[FEATURE_SELECTION_PRESELECT].length
				];
			dispatch(selectExclusively(
				FEATURE_SELECTIONS,
				targetSelection,
				targetId
			));
		},
		[targetSelection, appSource.ids, selections, dispatch]
	);

	if (
		(!navPos.app) ||
		(!appSource && !selections[FEATURE_SELECTION_PRESELECT].length)
	) {
		return null;
	}

	const showWings = !goZero && !navPos.listMorePoi && isPoiOnTour;

	const withName = navPos.map || navPos.list;
	const text =
		(navPos.listMorePoi || !isPoiOnTour) && !goZero ? translate('stade.tour.getBack') :
			(withName ?
					detailsFeature?.properties?.name || translate('stade.tour.start') :
					(goZero || selections[FEATURE_SELECTION_PRESELECT].idx < 0 ?
						translate(goZero ? 'stade.tour.start' : 'stade.tour.startshort') :
						`${selections[FEATURE_SELECTION_PRESELECT].idx + 1} / ${selections[FEATURE_SELECTION_PRESELECT].length || appSource?.ids?.length}`)
			);
	const prev = withName ?
		'<' :
		translate('stade.tour.prev');
	const next = withName ?
		'>' :
		translate('stade.tour.next');

	return (<div className={`stade-widget ${modClasses("stade-widget__poi-cycling", {'with-name': withName})}`} >
		<ul>
			<li>
				{showWings && <button onClick={selectPrev}>{prev}</button>}
			</li>
			{!showWings || navPos.map ?
				<li><button onClick={selectCurr}>{text}</button></li> :
				<li>{text}</li>
			}
			<li>
				{showWings && <button onClick={selectNext}>{next}</button>}
			</li>
		</ul>
	</div>);
}

export default memo(PoiCycling);
